/**
 * Consignments from both ends — the owner's offer, the gallery's inventory and
 * ledger — and the sale made under one.
 */
import type {
  EndConsignmentRequest,
  OfferConsignmentRequest,
  WorkConsignmentResponse,
  AnswerRevisionRequest,
  ArrangementsResponse,
  AnswerConsignmentRequest,
  BuyerCheckRequest,
  BuyerCheckResponse,
  ConsignedWorkLocationResponse,
  ConsignmentRevisionsResponse,
  EffectSaleRequest,
  GalleryInventoryResponse,
  GalleryPlacementsResponse,
  ProposeRevisionRequest,
  SetConsignmentLocationSharingRequest,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

/**
 * The owner opens or closes the consignee firm's view of where the work is —
 * OND-88. A setting on the owner's own record; the answer is the switch as it
 * now stands, so the panel draws the server's word and not its own.
 */
export const setConsignmentLocationSharing = (
  consignmentId: string,
  body: SetConsignmentLocationSharingRequest,
) =>
  requestWith<{ locationShared: boolean }>(
    "PUT",
    `/consignments/${consignmentId}/location-sharing`,
    body,
  );

/**
 * The gallery's one question about one consignment — where is it, or where
 * was it when we sold it. Null when the firm may read neither.
 */
export const fetchConsignedWorkLocation = (consignmentId: string) =>
  request<ConsignedWorkLocationResponse>(`/consignments/${consignmentId}/location`).then(
    (body) => body.location,
  );

/**
 * Who is holding this work, on what terms — the owner's side of a consignment.
 * Null is the ordinary case. Readable by whoever could share the work.
 */
export const fetchWorkConsignment = (workId: string) =>
  request<WorkConsignmentResponse>(`/works/${workId}/consignment`).then(
    (body) => body.consignment,
  );

/**
 * Offer a work to a gallery. The owner's act, or an editor-rung agent's — the
 * set `ondo.offer_consignment()` takes and `consign_work` now mirrors. Had no
 * caller anywhere in the product from 20260902090000 until this.
 */
export const offerConsignment = (workId: string, body: OfferConsignmentRequest) =>
  request<void>(`/works/${workId}/consignments`, body);

/** Recall a consignment, or record that it ended. Ends the row; never deletes it. */
export const endConsignment = (consignmentId: string, body: EndConsignmentRequest) =>
  request<void>(`/consignments/${consignmentId}/end`, body);

/**
 * The gallery's own inventory: live consignments, offers and accepted both.
 *
 * ONE request rather than two. `ondo.gallery_inventory()` returns every live
 * row and `acceptedAt` is what tells an unanswered offer from a work the gallery
 * is holding — so the split is made once, in the model, rather than by asking
 * the server the same question twice with different filters.
 */
export const fetchGalleryInventory = () =>
  request<GalleryInventoryResponse>("/consignments/mine").then(
    (body) => body.consignments,
  );

/**
 * Every version of one consignment's terms, and which of them is in force.
 *
 * ONE request returning both halves, because the screen shows both: the terms
 * being changed are not the previous revision — on a first repricing there is
 * no previous revision at all — so `terms` answers "what is in force" whether
 * or not anything has ever been proposed.
 */
export const fetchConsignmentRevisions = (consignmentId: string) =>
  request<ConsignmentRevisionsResponse>(`/consignments/${consignmentId}/revisions`);

/**
 * Propose a new version of the terms. Either party, on a live consignment.
 *
 * It changes nothing about what the gallery may sell for: that happens when the
 * other side accepts, in the same transaction as their answer.
 */
export const proposeConsignmentRevision = (
  consignmentId: string,
  body: ProposeRevisionRequest,
) => request<void>(`/consignments/${consignmentId}/revisions`, body);

/**
 * Accept or refuse one exact version.
 *
 * SAFE TO REPEAT, which is worth saying at the boundary a double-pressed button
 * reaches: a party answers once, and a second call returns without writing
 * anything. So this never needs a client-side guard against a second press to
 * stay correct — the busy flag is about the reader's experience, not the data.
 */
export const answerConsignmentRevision = (
  consignmentId: string,
  revisionId: string,
  accept: boolean,
) =>
  request<void>(
    `/consignments/${consignmentId}/revisions/${revisionId}/answer`,
    { accept } satisfies AnswerRevisionRequest,
  );

/** The gallery accepting or declining a work offered to it. */
export const answerConsignment = (id: string, accept: boolean) =>
  request<void>(
    `/consignments/${id}/answer`,
    { accept } satisfies AnswerConsignmentRequest,
  );

/**
 * The gallery's ledger of what it has placed — Sold Works.
 *
 * A SECOND read rather than a filter over the inventory, and that is the shape
 * of the domain rather than a routing choice. `ondo.gallery_inventory()` returns
 * live consignments; a sale ENDS the consignment and writes an
 * `ondo.transfers` row, so a sold work is not a differently-flagged inventory
 * row — it is a different table, reached through a different relation
 * (`ondo.placed_work_ids()`), and one screen's emptiness says nothing about the
 * other's.
 */
export const fetchGalleryPlacements = () =>
  request<GalleryPlacementsResponse>("/gallery/placements").then(
    (body) => body.placements,
  );

/**
 * Selling a work, addressed to the CONSIGNMENT rather than to the work.
 *
 * The consignment is the authority the sale is made under — a work with no live
 * accepted one has no door at all — so the route names it instead of making the
 * server go looking.
 */
export const effectSale = (consignmentId: string, body: EffectSaleRequest) =>
  request<void>(`/consignments/${consignmentId}/sale`, body);

/**
 * Is this the buyer you meant? Asked on blur, before the sale.
 *
 * Scoped to the CONSIGNMENT for the same reason the sale is: it is the authority
 * the question is asked under, and a gallery holding no live consignment has no
 * door here either. Both end at the same three tests in SQL, so the name shown
 * and the sale made cannot disagree.
 *
 * NOT A SEARCH. It takes an id and answers with a name; there is no direction in
 * which it runs backwards, and no endpoint may be added that takes the name.
 */
export const checkBuyer = (consignmentId: string, body: BuyerCheckRequest) =>
  request<BuyerCheckResponse>(`/consignments/${consignmentId}/buyer-check`, body);

/**
 * What is out across every work the caller owns — mandates and consignments,
 * one list. The owner's mirror of `fetchGalleryInventory`. Live rows only.
 */
export const fetchArrangements = () =>
  request<ArrangementsResponse>("/consignments/arrangements").then(
    (body) => body.arrangements,
  );
